import { useEffect, useState, useCallback } from "react";
import { Search, RotateCcw, Trash2, ShieldAlert, ArchiveRestore, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { ErrorView, EmptyState } from "@/components/StateViews";
import { TableSkeleton } from "@/components/Skeletons";
import { fmtDateTime } from "@/lib/format";

const ACTION_META = {
  deleted: { label: "Moved to bin", icon: Trash2, cls: "bg-amber-50 text-amber-700" },
  restored: { label: "Restored", icon: RotateCcw, cls: "bg-emerald-50 text-emerald-700" },
  permanent_delete: { label: "Permanent delete", icon: ShieldAlert, cls: "bg-red-50 text-red-700" },
  quarantined: { label: "Quarantined", icon: ArchiveRestore, cls: "bg-slate-100 text-slate-600" },
};
const ACTIONS = [
  { v: "", label: "All actions" }, { v: "deleted", label: "Moved to bin" },
  { v: "restored", label: "Restored" }, { v: "permanent_delete", label: "Permanent delete" },
  { v: "quarantined", label: "Quarantined" },
];

function ActionBadge({ action }) {
  const m = ACTION_META[action] || { label: action, icon: Trash2, cls: "bg-mist text-ink/60" };
  const Icon = m.icon;
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${m.cls}`}><Icon className="h-3 w-3" />{m.label}</span>;
}

export default function DeletionLog() {
  const [search, setSearch] = useState("");
  const [action, setAction] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = { page, page_size: 25 };
      if (search.trim()) params.search = search.trim();
      if (action) params.action = action;
      const { data } = await api.get("/leads/deletion-log", { params });
      setData(data.data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [page, search, action]);

  useEffect(() => { const t = setTimeout(load, search ? 300 : 0); return () => clearTimeout(t); }, [load, search]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  const setF = (fn) => { fn(); setPage(1); };

  return (
    <div className="space-y-4" data-testid="deletion-log">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
          <input data-testid="log-search" value={search} onChange={(e) => setF(() => setSearch(e.target.value))} placeholder="Search lead, reason or admin…" className="h-10 w-64 rounded-xl border border-sky bg-white pl-9 pr-3 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20" />
        </div>
        <select data-testid="log-action-filter" value={action} onChange={(e) => setF(() => setAction(e.target.value))} className="h-10 rounded-xl border border-sky bg-white px-2.5 text-sm text-navy outline-none focus:border-loop">
          {ACTIONS.map((a) => <option key={a.v} value={a.v}>{a.label}</option>)}
        </select>
        {data && <span className="text-sm text-ink/50">{data.total} entries</span>}
      </div>

      {loading && !data ? <TableSkeleton rows={6} cols={5} /> : error ? <ErrorView message="Couldn't load the deletion log." onRetry={load} /> :
        !data?.items.length ? <EmptyState title="No deletion activity yet" subtitle="Deletes, restores and permanent removals are recorded here." /> : (
          <>
            <div className="overflow-hidden rounded-2xl border border-sky/70 bg-white">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-mist text-left text-xs font-semibold uppercase tracking-wide text-ink/50">
                    <tr>{["Action", "Lead", "By", "Reason / details", "When"].map((h) => <th key={h} className="whitespace-nowrap px-4 py-3">{h}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-sky/50">
                    {data.items.map((r) => (
                      <tr key={r.id} data-testid={`log-row-${r.id}`}>
                        <td className="px-4 py-3"><ActionBadge action={r.action} /></td>
                        <td className="px-4 py-3 font-medium text-navy">{r.lead_name || (r.action === "permanent_delete" ? `${r.count || 0} lead(s)` : "—")}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-ink/70">{r.by_user_name || "System"}</td>
                        <td className="px-4 py-3 text-xs text-ink/60">
                          {r.reason || "—"}
                          {r.action === "permanent_delete" && (
                            <span className="ml-1 text-ink/45">· {r.hard_deleted || 0} hard, {r.anonymized || 0} anonymized{r.scope ? ` (${r.scope})` : ""}</span>
                          )}
                          {r.note && <span className="ml-1 italic text-ink/45">“{r.note}”</span>}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-ink/55">{fmtDateTime(r.at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-ink/50">Page {data.page} of {totalPages}</p>
                <div className="flex gap-2">
                  <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="flex items-center gap-1 rounded-lg border border-sky bg-white px-3 py-2 text-sm font-medium text-navy disabled:opacity-40"><ChevronLeft className="h-4 w-4" />Prev</button>
                  <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="flex items-center gap-1 rounded-lg border border-sky bg-white px-3 py-2 text-sm font-medium text-navy disabled:opacity-40">Next<ChevronRight className="h-4 w-4" /></button>
                </div>
              </div>
            )}
          </>
        )}
    </div>
  );
}
